/**
 * Rest-day / PH OIL entitlement from verified clock hours.
 *
 * Work under 4 hours on a non-working day earns 0.5 day, not 1.0.
 * Saturday-PH MOM force-full-day and weekday-after-hours are exempt
 * (handled by the caller via `forceFullDay` / `restrict05`).
 *
 * Mirror: staff_core/services/special_leave_service.py
 *   OIL_FULL_DAY_MIN_HOURS / apply_oil_hours_cap
 */
export const OIL_FULL_DAY_MIN_HOURS = 4;

/**
 * @param {Array<object>} events
 * @returns {{
 *   hasClockIn: boolean,
 *   hasClockOut: boolean,
 *   hasPair: boolean,
 *   hours: number|null,
 *   clockIn: string|null,
 *   clockOut: string|null,
 * }}
 */
export function summarizeOilAttendance(events) {
  const list = Array.isArray(events) ? events : [];
  const typeOf = (ev) => ev?.clock_event_type || ev?.event_type;
  const tsOf = (ev) => {
    if (ev?.event_time) return ev.event_time;
    const raw = ev?.clock_event_timestamp || ev?.local_timestamp || ev?.timestamp;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };
  const sortKey = (ev) => {
    const raw = ev?.clock_event_timestamp || ev?.local_timestamp || ev?.timestamp || ev?.event_time;
    const d = raw ? new Date(raw) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  };

  const ordered = [...list].sort((a, b) => sortKey(a) - sortKey(b));
  const ins = ordered.filter((ev) => typeOf(ev) === 'clock_in');
  const outs = ordered.filter((ev) => typeOf(ev) === 'clock_out');
  const firstIn = ins[0] || null;
  const lastOut = outs.length ? outs[outs.length - 1] : null;

  let hours = null;
  if (firstIn && lastOut) {
    const start = new Date(firstIn.clock_event_timestamp || firstIn.local_timestamp || firstIn.timestamp);
    const end = new Date(lastOut.clock_event_timestamp || lastOut.local_timestamp || lastOut.timestamp);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
      hours = Math.round(((end.getTime() - start.getTime()) / 3600000) * 100) / 100;
    }
  }

  return {
    hasClockIn: Boolean(firstIn),
    hasClockOut: Boolean(lastOut),
    hasPair: Boolean(firstIn && lastOut && hours != null && hours > 0),
    hours,
    clockIn: firstIn ? (tsOf(firstIn) || '').slice(0, 5) : null,
    clockOut: lastOut ? (tsOf(lastOut) || '').slice(0, 5) : null,
  };
}

/**
 * @param {{ hours?: number|null, hasPair?: boolean, forceFullDay?: boolean, restrict05?: boolean }} opts
 * @returns {boolean}
 */
export function shouldCapOilToHalfDay(opts = {}) {
  if (opts.forceFullDay || opts.restrict05) return false;
  if (!opts.hasPair || opts.hours == null) return false;
  return Number(opts.hours) < OIL_FULL_DAY_MIN_HOURS;
}

/**
 * @param {number|string|null|undefined} hours
 * @returns {string}
 */
export function formatOilWorkedHours(hours) {
  if (hours == null || hours === '') return '';
  const n = Number(hours);
  if (Number.isNaN(n)) return '';
  const totalMinutes = Math.round(n * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
